import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import API_BASE_URL from '../config/api';

// Create context for permission management
export const PermissionContext = createContext();

// Custom hook for easy access to permission context
export const usePermissions = () => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// PermissionProvider component to manage user permissions across the app
export const PermissionProvider = ({ children }) => {
  // Initialize from localStorage immediately for faster UI response
  const [permissions, setPermissions] = useState([]);
  const [isAdmin, setIsAdmin] = useState(() => localStorage.getItem('isAdmin') === 'true');
  const [isOwner, setIsOwner] = useState(() => localStorage.getItem('isOwner') === 'true');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user permissions from API
  const fetchPermissions = useCallback(async () => {
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      setLoading(false);
      return;
    }

    // First, immediately update from localStorage (in case login just stored values)
    const storedIsAdmin = localStorage.getItem('isAdmin') === 'true';
    const storedIsOwner = localStorage.getItem('isOwner') === 'true';
    setIsAdmin(storedIsAdmin);
    setIsOwner(storedIsOwner);

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/AccessControl/my-access`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized: Please log in again.');
        }
        throw new Error(`Failed to fetch permissions: ${response.status}`);
      }

      const data = await response.json();
      console.log('[RBAC] API Response:', { success: data.success, isAdmin: data.isAdmin, isOwner: data.isOwner, interfaceCount: data.interfaces?.length });
      if (data.success) {
        setPermissions(data.interfaces || []);
        setIsAdmin(data.isAdmin || false);
        setIsOwner(data.isOwner || false);
        // Also update localStorage with API response
        localStorage.setItem('isAdmin', (data.isAdmin || false).toString());
        localStorage.setItem('isOwner', (data.isOwner || false).toString());
        console.log('[RBAC] Permissions set:', { isAdmin: data.isAdmin, isOwner: data.isOwner, interfaces: data.interfaces?.length });
      } else {
        throw new Error(data.message || 'Failed to fetch permissions');
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setError(error.message);
      // Already set from localStorage above, no need to set again
    } finally {
      setLoading(false);
    }
  }, []);

  // Check if user has access to a specific interface by key
  const hasAccess = useCallback((interfaceKey) => {
    // Admins and owners have access to everything
    if (isAdmin || isOwner) {
      return true;
    }
    // Check if interface is in the permitted list
    return permissions.some(p => p.interfaceKey === interfaceKey);
  }, [permissions, isAdmin, isOwner]);

  // Check if user has access to a specific path
  const hasAccessToPath = useCallback((path) => {
    // Admins and owners have access to everything
    if (isAdmin || isOwner) {
      return true;
    }
    // Normalize: strip /dashboard/ prefix so DB paths ("/dashboard/x") match frontend slugs ("x")
    const normalize = (p) => (p || '').replace(/^\/dashboard\//, '');
    const normalizedPath = normalize(path);
    // Check path match OR interfaceKey match (interfaceKey is always the slug)
    return permissions.some(p => normalize(p.path) === normalizedPath || p.interfaceKey === normalizedPath);
  }, [permissions, isAdmin, isOwner]);

  // Get list of interface keys user has access to
  const getPermittedInterfaceKeys = useCallback(() => {
    return permissions.map(p => p.interfaceKey);
  }, [permissions]);

  // Get list of paths user has access to
  const getPermittedPaths = useCallback(() => {
    return permissions.map(p => p.path);
  }, [permissions]);

  // Update permissions when user logs in
  const updatePermissionsFromLogin = useCallback((loginResponse) => {
    if (loginResponse.isAdmin !== undefined) {
      setIsAdmin(loginResponse.isAdmin);
      localStorage.setItem('isAdmin', loginResponse.isAdmin.toString());
    }
    if (loginResponse.isOwner !== undefined) {
      setIsOwner(loginResponse.isOwner);
      localStorage.setItem('isOwner', loginResponse.isOwner.toString());
    }
    // Fetch full permissions from API
    fetchPermissions();
  }, [fetchPermissions]);

  // Clear permissions on logout
  const clearPermissions = useCallback(() => {
    setPermissions([]);
    setIsAdmin(false);
    setIsOwner(false);
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('isOwner');
  }, []);

  // Fetch permissions on mount if user is logged in
  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
      // Fetch fresh data from API (this also reads from localStorage first)
      fetchPermissions();
    } else {
      setLoading(false);
    }
  }, [fetchPermissions]);

  // Context value
  const value = {
    permissions,
    isAdmin,
    isOwner,
    loading,
    error,
    hasAccess,
    hasAccessToPath,
    getPermittedInterfaceKeys,
    getPermittedPaths,
    fetchPermissions,
    updatePermissionsFromLogin,
    clearPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export default PermissionProvider;
